// src/features/journal/context/JournalContext.jsx
// Thin context wrapper around journal services

import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import * as journalService from '../services/journalService';
import { LoadingState, SyncStatus } from '@/types';
import logger from '@/shared/utils/core/logger';
import { useSubscriptionContext } from '../../subscription/context/SubscriptionContext';
import { FREE_ENTRY_LIMIT } from '../../subscription/services/subscriptionService';


// Pagination constants - 10 entries per page
const PAGE_SIZE = 10;

const JournalContext = createContext({
  entries: [],
  paginatedEntries: [],
  loading: LoadingState.IDLE,
  syncStatus: SyncStatus.SYNCED,
  error: null,
  addEntry: async () => {},
  retrySync: async () => {},
  loadMoreEntries: async () => {},
  hasMoreEntries: true,
  loadingMore: false,
  pendingCount: 0,
  isOnline: navigator.onLine,
  // Authoritative total from Firestore (userAIMemory.statistics.totalEntries)
  entryCount: 0,
  // Pagination
  currentPage: 1,
  totalPages: 1,
  pageSize: PAGE_SIZE,
  goToPage: () => {},
  nextPage: () => {},
  prevPage: () => {},
  hasNextPage: false,
  hasPrevPage: false,
});

export function JournalProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();
  const { isPremium, entryCount, openPaywall } = useSubscriptionContext();

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(LoadingState.LOADING);
  const [syncStatus, setSyncStatus] = useState(SyncStatus.SYNCED);
  const [error, setError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Load more state
  const [hasMoreEntries, setHasMoreEntries] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  const syncInProgressRef = useRef(false);
  const unsubscribeRef = useRef(null);

  // Clear error after a timeout
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Sync pending entries via API
  const syncPending = useCallback(async () => {
    logger.debug('[JournalContext] syncPending called:', {
      hasAuthUser: !!authUser,
      isOnline: navigator.onLine,
      syncInProgress: syncInProgressRef.current
    });

    if (!authUser || !navigator.onLine || syncInProgressRef.current) {
      logger.debug('[JournalContext] Sync blocked - conditions not met');
      return;
    }

    syncInProgressRef.current = true;
    setSyncStatus(SyncStatus.PENDING);

    try {
      const allPending = await journalService.loadPendingEntries();
      // Only sync entries belonging to the current authenticated user (prevents cross-user data upload)
      const pendingEntries = allPending.filter(e => e.uid === authUser.uid);

      if (pendingEntries.length === 0) {
        setSyncStatus(SyncStatus.SYNCED);
        return;
      }

      const { successfulSyncs, failedSyncs, permanentFailures } = await journalService.syncPendingEntries(
        authUser,
        pendingEntries
      );

      // Remove both successful syncs and permanent failures from the UI.
      // Permanent failures are non-retryable (e.g. 403 free_limit_reached) and have already
      // been removed from IndexedDB — keep them in UI showing a stuck pending state is worse.
      const removedTempIds = new Set([
        ...successfulSyncs,
        ...(permanentFailures || []).map(f => f.entry?._tempId).filter(Boolean),
      ]);
      if (removedTempIds.size > 0) {
        setEntries(prev => prev.filter(entry =>
          !entry._tempId || !removedTempIds.has(entry._tempId)
        ));
      }

      // Surface permanent failures to the user
      if (permanentFailures?.length > 0) {
        const hasFreeLimitError = permanentFailures.some(f => f.errorCode === 'free_limit_reached');
        if (hasFreeLimitError) {
          openPaywall();
        } else {
          setError(`${permanentFailures.length} ${permanentFailures.length === 1 ? 'entry' : 'entries'} could not be saved`);
        }
      }

      setSyncStatus(failedSyncs.length > 0 ? SyncStatus.ERROR : SyncStatus.SYNCED);

      if (failedSyncs.length > 0) {
        setError(`${failedSyncs.length} entries failed to sync`);
      }

    } catch (syncError) {
      logger.error('[Journal] Sync failed:', syncError);
      setSyncStatus(SyncStatus.ERROR);
      setError('Failed to sync entries. Will retry when online.');
    } finally {
      syncInProgressRef.current = false;
    }
  }, [authUser, openPaywall]);

  // Load pending entries from IndexedDB, scoped to the current user
  useEffect(() => {
    if (!authUser) return;
    const loadPending = async () => {
      const pending = await journalService.loadPendingEntries();
      // Only show entries that belong to the authenticated user (defense-in-depth against shared-device leakage)
      const userPending = pending.filter(e => e.uid === authUser.uid);
      if (userPending.length > 0) {
        setEntries(userPending);
        setSyncStatus(SyncStatus.OFFLINE);
      }
    };

    loadPending();
  }, [authUser?.uid]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setSyncStatus(SyncStatus.PENDING);
      syncPending();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setSyncStatus(SyncStatus.OFFLINE);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial sync if online
    if (navigator.onLine) {
      syncPending();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPending]);

  // Listen for TRIGGER_SYNC from service worker (background sync event)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleSWMessage = (event) => {
      if (event.data?.type === 'TRIGGER_SYNC') {
        syncPending(); // syncPending guards authUser + onLine internally
      }
    };

    navigator.serviceWorker.addEventListener('message', handleSWMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, [syncPending]); // authUser not needed — syncPending re-creates when authUser changes

  // Subscribe to Firestore for real-time updates
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      setLoading(LoadingState.LOADING);
      return;
    }

    if (!authUser) {
      setLoading(LoadingState.IDLE);
      setEntries([]); // Clear entries when user logs out
      setHasMoreEntries(true); // Reset pagination so next login fetches fresh
      return;
    }

    setLoading(LoadingState.LOADING);

    unsubscribeRef.current = journalService.subscribeToEntries(
      authUser.uid,
      (remoteEntries) => {
        setEntries(prevEntries => {
          logger.debug(`[JournalContext] Firestore update: ${remoteEntries.length} remote entries`);

          // Keep pending entries (those with _tempId) that haven't synced yet
          const pendingEntries = prevEntries.filter(entry => entry._tempId);
          logger.debug(`[JournalContext] Found ${pendingEntries.length} pending entries`);

          // Remove duplicates: If a pending entry has synced, its remote version will have the same content/timestamp
          // We match by content to detect duplicates (pending entries that already synced)
          const remotePendingMatch = (pending, remote) => {
            // Get content from either 'content' or 'extractedText' (for OCR entries)
            const pendingText = (pending.content || pending.extractedText || '').substring(0, 100).trim();
            const remoteText = (remote.content || remote.extractedText || '').substring(0, 100).trim();

            // If both are empty, don't match (could be different empty entries)
            if (!pendingText || !remoteText) return false;

            // Content must match exactly (first 100 chars)
            if (pendingText !== remoteText) return false;

            // Check timestamps are within 10 seconds (accounting for client/server time diff)
            if (pending.createdAt && remote.createdAt) {
              try {
                const pendingTime = pending.createdAt.toMillis ? pending.createdAt.toMillis() : new Date(pending.createdAt).getTime();
                const remoteTime = remote.createdAt.toMillis ? remote.createdAt.toMillis() : new Date(remote.createdAt).getTime();

                // Handle invalid timestamps
                if (isNaN(pendingTime) || isNaN(remoteTime)) return false;

                const timeDiff = Math.abs(pendingTime - remoteTime);
                return timeDiff < 120000; // Within 2 minutes (accounts for network latency + clock drift)
              } catch (error) {
                logger.warn('[JournalContext] Error comparing timestamps:', error);
                return false;
              }
            }

            // Don't match without timestamp validation - prevents false positives
            if (!pending.createdAt || !remote.createdAt) {
              logger.warn('[JournalContext] Cannot validate duplicate without timestamps:', {
                pending: pending._tempId,
                remote: remote.id
              });
              return false;
            }
            return false;
          };

          // Filter out pending entries that have already been synced
          const unsyncedPendingEntries = pendingEntries.filter(pending =>
            !remoteEntries.some(remote => remotePendingMatch(pending, remote))
          );

          const matchedCount = pendingEntries.length - unsyncedPendingEntries.length;
          if (matchedCount > 0) {
            logger.debug(`[JournalContext] Removed ${matchedCount} pending entries that have been synced`);
          }

          // Merge and sort entries by timestamp (newest first)
          const finalEntries = [...unsyncedPendingEntries, ...remoteEntries]
            .sort((a, b) => {
              // Handle missing timestamps - move to end
              if (!a.createdAt && !b.createdAt) return 0;
              if (!a.createdAt) return 1;
              if (!b.createdAt) return -1;

              // Compare timestamps
              try {
                const aTime = a.createdAt?.toMillis?.() ?? new Date(a.createdAt).getTime();
                const bTime = b.createdAt?.toMillis?.() ?? new Date(b.createdAt).getTime();

                if (isNaN(aTime) || isNaN(bTime)) return 0;

                return bTime - aTime; // Descending (newest first)
              } catch (error) {
                logger.error('[JournalContext] Error sorting entries:', error);
                return 0;
              }
            });

          logger.debug(`[JournalContext] Final entry count: ${finalEntries.length} (${unsyncedPendingEntries.length} pending + ${remoteEntries.length} remote)`);

          // Validate all entries have timestamps
          const invalidEntries = finalEntries.filter(e => !e.createdAt);
          if (invalidEntries.length > 0) {
            logger.error('[JournalContext] Found entries without createdAt:', invalidEntries.map(e => ({
              id: e.id || e._tempId,
              content: (e.content || e.extractedText || '').substring(0, 50)
            })));
          }

          return finalEntries;
        });

        // Track if there are more entries to load
        if (remoteEntries.length < 50) {
          setHasMoreEntries(false);
        }

        setLoading(LoadingState.SUCCESS);
        setError(null);
      },
      (firestoreError) => {
        setLoading(LoadingState.ERROR);
        setError('Failed to load entries. Please refresh.');
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [authUser?.uid, authLoading]); // Re-subscribe when user changes

  // Add entry with optimistic updates
  const addEntry = useCallback(
    async (entryData) => {
      if (!authUser) throw new Error('Authentication required');

      // Free trial gate — show paywall before creating the entry (client-side check)
      if (!isPremium && entryCount >= FREE_ENTRY_LIMIT) {
        openPaywall();
        throw new Error('SUBSCRIPTION_REQUIRED');
      }

      try {
        // Create entry and add to pending queue
        const tempEntry = await journalService.createEntry(authUser, entryData);

        // Optimistic update - add to UI immediately
        setEntries(prev => [tempEntry, ...prev]);

        // If online, sync immediately
        if (navigator.onLine) {
          await syncPending();
          // Mark save time so FCM toasts and FloatingInsight can suppress themselves briefly
          // Guarded: WKWebView with ITP can throw on sessionStorage access.
          try { sessionStorage.setItem('clarity_last_entry_saved', Date.now().toString()); } catch { /* ignore */ }
        } else {
          setSyncStatus(SyncStatus.OFFLINE);
        }

        return tempEntry;
      } catch (error) {
        // Server-side gate hit (backup check) — show paywall
        if (error?.message === 'free_limit_reached' || error?.code === 'free_limit_reached') {
          openPaywall();
          throw new Error('SUBSCRIPTION_REQUIRED');
        }
        logger.error('[Journal] Failed to add entry:', error);
        setError(`Failed to save entry: ${error.message}`);
        throw error;
      }
    },
    [authUser, syncPending, isPremium, entryCount, openPaywall]
  );

  // Manual retry sync
  const retrySync = useCallback(async () => {
    if (syncInProgressRef.current) return;
    await syncPending();
  }, [syncPending]);

  // Load more older entries
  const loadMoreEntries = useCallback(async () => {
    if (loadingMore || !hasMoreEntries || !authUser) return;

    setLoadingMore(true);

    try {
      // Get oldest entry's timestamp
      const oldestEntry = entries[entries.length - 1];
      if (!oldestEntry) {
        setHasMoreEntries(false);
        return;
      }

      // Only paginate using a Firestore Timestamp — pending entries have a JS Date or null
      // which causes unpredictable behaviour with Firestore's startAfter cursor.
      const cursorTimestamp = oldestEntry.createdAt?.toMillis ? oldestEntry.createdAt : null;
      if (!cursorTimestamp) {
        // Oldest visible entry is pending (not yet synced); nothing older to fetch from Firestore.
        return;
      }

      // Load next batch
      const newEntries = await journalService.loadOlderEntries(
        authUser.uid,
        cursorTimestamp,
        50
      );

      if (newEntries.length === 0) {
        setHasMoreEntries(false);
      } else {
        // Append to existing entries
        setEntries(prev => [...prev, ...newEntries]);

        if (newEntries.length < 50) {
          setHasMoreEntries(false);
        }
      }
    } catch (error) {
      logger.error('[JournalContext] Failed to load more entries:', error);
      setError('Failed to load older entries');
    } finally {
      setLoadingMore(false);
    }
  }, [authUser, entries, loadingMore, hasMoreEntries]);

  // Refresh pending entries from IndexedDB (useful after updating a draft)
  const refreshPendingEntries = useCallback(async () => {
    const allPending = await journalService.loadPendingEntries();
    // Only show entries belonging to the current user
    const pending = authUser ? allPending.filter(e => e.uid === authUser.uid) : [];
    setEntries(prevEntries => {
      // Keep remote entries (those without _tempId or with both id and _tempId)
      const remoteEntries = prevEntries.filter(entry => !entry._tempId || entry.id);
      // Merge with fresh pending entries
      return [...pending, ...remoteEntries].sort((a, b) => {
        try {
          const aTime = a.createdAt?.toMillis?.() ?? new Date(a.createdAt).getTime();
          const bTime = b.createdAt?.toMillis?.() ?? new Date(b.createdAt).getTime();
          if (isNaN(aTime) || isNaN(bTime)) return 0;
          return bTime - aTime; // Descending (newest first)
        } catch (error) {
          return 0;
        }
      });
    });
  }, [authUser]);

  // Delete entry (with optimistic updates and rollback on error)
  const deleteEntry = useCallback(
    async (entryId) => {
      if (!authUser) throw new Error('Authentication required');
      if (!entryId) throw new Error('Entry ID required');

      // Find entry for rollback purposes
      const entry = entries.find(e => e.id === entryId || e._tempId === entryId);
      if (!entry) {
        throw new Error('Entry not found');
      }

      try {
        // Optimistic update - remove from UI immediately
        setEntries(prev => prev.filter(e =>
          e.id !== entryId && e._tempId !== entryId
        ));

        // Perform deletion
        const result = await journalService.deleteEntry(entryId, authUser, entries);

        logger.debug(`[JournalContext] Successfully deleted ${result.isDraft ? 'draft' : 'entry'}`);

        return result;

      } catch (error) {
        // Rollback optimistic update on error
        logger.error('[JournalContext] Delete failed, rolling back:', error);

        // Restore entry to list (avoid duplicates)
        setEntries(prev => {
          const exists = prev.find(e => e.id === entryId || e._tempId === entryId);
          if (exists) return prev;

          // Add back in correct sorted position
          return [...prev, entry].sort((a, b) => {
            try {
              const aTime = a.createdAt?.toMillis?.() ?? new Date(a.createdAt).getTime();
              const bTime = b.createdAt?.toMillis?.() ?? new Date(b.createdAt).getTime();
              if (isNaN(aTime) || isNaN(bTime)) return 0;
              return bTime - aTime;
            } catch (sortError) {
              return 0;
            }
          });
        });

        throw error; // Re-throw for UI to handle
      }
    },
    [authUser, entries]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      syncInProgressRef.current = false;
    };
  }, []);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(entries.length / PAGE_SIZE));
  const paginatedEntries = entries.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Pagination handlers
  const goToPage = useCallback((page) => {
    const validPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(validPage);
  }, [totalPages]);

  const nextPage = useCallback(() => {
    if (hasNextPage) setCurrentPage(p => p + 1);
  }, [hasNextPage]);

  const prevPage = useCallback(() => {
    if (hasPrevPage) setCurrentPage(p => p - 1);
  }, [hasPrevPage]);

  // Reset to page 1 when entries change significantly (e.g., new entry added)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [totalPages, currentPage]);

  const pendingCount = entries.filter(entry => entry._tempId).length;

  const contextValue = useMemo(() => ({
    entries,
    paginatedEntries,
    loading,
    syncStatus,
    error,
    addEntry,
    deleteEntry,
    retrySync,
    refreshPendingEntries,
    loadMoreEntries,
    hasMoreEntries,
    loadingMore,
    pendingCount,
    isOnline,
    // Single source of truth for total entry count — sourced from
    // userStats/{uid}.totalEntries via SubscriptionContext.
    // Use this instead of entries.length, which only reflects the paginated local list.
    entryCount,
    currentPage,
    totalPages,
    pageSize: PAGE_SIZE,
    goToPage,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
  }), [
    entries, paginatedEntries, loading, syncStatus, error,
    addEntry, deleteEntry, retrySync, refreshPendingEntries,
    loadMoreEntries, hasMoreEntries, loadingMore, pendingCount, isOnline,
    entryCount, currentPage, totalPages, goToPage, nextPage, prevPage,
    hasNextPage, hasPrevPage,
  ]);

  return (
    <JournalContext.Provider value={contextValue}>
      {children}
    </JournalContext.Provider>
  );
}

export function useJournal() {
  const context = useContext(JournalContext);
  if (!context) {
    throw new Error('useJournal must be used within a JournalProvider');
  }
  return context;
}
